import { supabase } from '@/integrations/supabase/client';

export interface NotificationSound {
  id: string;
  user_id: string;
  sound_name: string;
  file_path: string;
  is_active: boolean;
  file_size: number;
  created_at: string;
  updated_at: string;
}

const MAX_SOUNDS_PER_USER = 5;
const MAX_FILE_SIZE = 500 * 1024; // 500KB
const ALLOWED_FORMATS = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg'];
const MAX_DURATION_SECONDS = 10;

export const notificationSoundService = {
  // Get all sounds for current user
  async getUserSounds(userId: string): Promise<NotificationSound[]> {
    const { data, error } = await supabase
      .from('user_notification_sounds')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching user sounds:', error);
      throw error;
    }

    return data || [];
  },

  // Get active sound for user
  async getActiveSound(userId: string): Promise<NotificationSound | null> {
    const { data, error } = await supabase
      .from('user_notification_sounds')
      .select('*')
      .eq('user_id', userId)
      .eq('is_active', true)
      .single();

    if (error && error.code !== 'PGRST116') {
      console.error('Error fetching active sound:', error);
      throw error;
    }

    return data;
  },

  // Validate audio file
  async validateAudioFile(file: File): Promise<{ valid: boolean; error?: string }> {
    // Check file type
    if (!ALLOWED_FORMATS.includes(file.type)) {
      return { valid: false, error: 'invalidFormat' };
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return { valid: false, error: 'fileTooLarge' };
    }

    // Check duration using Web Audio API
    try {
      const duration = await this.getAudioDuration(file);
      if (duration > MAX_DURATION_SECONDS) {
        return { valid: false, error: 'durationTooLong' };
      }
    } catch (error) {
      console.error('Error checking audio duration:', error);
      return { valid: false, error: 'invalidFile' };
    }

    return { valid: true };
  },

  // Get audio duration
  async getAudioDuration(file: File): Promise<number> {
    return new Promise((resolve, reject) => {
      const audio = new Audio();
      audio.addEventListener('loadedmetadata', () => {
        resolve(audio.duration);
      });
      audio.addEventListener('error', reject);
      audio.src = URL.createObjectURL(file);
    });
  },

  // Check if user has reached max sounds limit
  async canAddMoreSounds(userId: string): Promise<boolean> {
    const sounds = await this.getUserSounds(userId);
    return sounds.length < MAX_SOUNDS_PER_USER;
  },

  // Upload sound file
  async uploadSound(
    userId: string,
    file: File,
    soundName: string
  ): Promise<NotificationSound> {
    // Validate file
    const validation = await this.validateAudioFile(file);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    // Check limit
    const canAdd = await this.canAddMoreSounds(userId);
    if (!canAdd) {
      throw new Error('maxSoundsReached');
    }

    // Create unique file path
    const fileExt = file.name.split('.').pop();
    const fileName = `${userId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

    // Upload to storage
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('notification-sounds')
      .upload(fileName, file, {
        contentType: file.type,
        upsert: false,
      });

    if (uploadError) {
      console.error('Error uploading sound:', uploadError);
      throw uploadError;
    }

    // Save metadata to database
    const { data, error } = await supabase
      .from('user_notification_sounds')
      .insert({
        user_id: userId,
        sound_name: soundName,
        file_path: uploadData.path,
        file_size: file.size,
        is_active: false,
      })
      .select()
      .single();

    if (error) {
      // Rollback: delete uploaded file
      await supabase.storage.from('notification-sounds').remove([fileName]);
      console.error('Error saving sound metadata:', error);
      throw error;
    }

    return data;
  },

  // Set active sound
  async setActiveSound(userId: string, soundId: string): Promise<void> {
    // Deactivate all sounds for user
    await supabase
      .from('user_notification_sounds')
      .update({ is_active: false })
      .eq('user_id', userId);

    // Activate selected sound
    const { error } = await supabase
      .from('user_notification_sounds')
      .update({ is_active: true })
      .eq('id', soundId)
      .eq('user_id', userId);

    if (error) {
      console.error('Error setting active sound:', error);
      throw error;
    }
  },

  // Deactivate all sounds (use default)
  async useDefaultSound(userId: string): Promise<void> {
    const { error } = await supabase
      .from('user_notification_sounds')
      .update({ is_active: false })
      .eq('user_id', userId);

    if (error) {
      console.error('Error deactivating sounds:', error);
      throw error;
    }
  },

  // Delete sound
  async deleteSound(userId: string, soundId: string): Promise<void> {
    // Get sound info
    const { data: sound, error: fetchError } = await supabase
      .from('user_notification_sounds')
      .select('file_path')
      .eq('id', soundId)
      .eq('user_id', userId)
      .single();

    if (fetchError) {
      console.error('Error fetching sound:', fetchError);
      throw fetchError;
    }

    // Delete from storage
    const { error: storageError } = await supabase.storage
      .from('notification-sounds')
      .remove([sound.file_path]);

    if (storageError) {
      console.error('Error deleting from storage:', storageError);
    }

    // Delete from database
    const { error: dbError } = await supabase
      .from('user_notification_sounds')
      .delete()
      .eq('id', soundId)
      .eq('user_id', userId);

    if (dbError) {
      console.error('Error deleting from database:', dbError);
      throw dbError;
    }
  },

  // Get public URL for sound
  async getSoundUrl(filePath: string): Promise<string> {
    const { data } = supabase.storage
      .from('notification-sounds')
      .getPublicUrl(filePath);

    return data.publicUrl;
  },
};
